import { useCallback, useEffect, useRef, useState } from "react";
import { buildViewerDocument, refreshViewer } from "../utils/dataLayerViewerDocument";

const REFRESH_INTERVAL = 1000;

/**
 * Opens a standalone browser window that mirrors window.dataLayer and refreshes it on an interval.
 */
export const useDataLayerViewer = () => {
	const windowRef = useRef<Window | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval>>();
	const [isViewerOpen, setViewerOpen] = useState(false);

	const stop = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = undefined;
		}
		windowRef.current = null;
		setViewerOpen(false);
	}, []);

	const closeViewer = useCallback(() => {
		if (windowRef.current && !windowRef.current.closed) {
			windowRef.current.close();
		}
		stop();
	}, [stop]);

	const openViewer = useCallback(() => {
		const viewerWindow = window.open(
			"",
			"dataLayerViewer",
			"width=800,height=600,scrollbars=yes,resizable=yes"
		);

		if (!viewerWindow) {
			return;
		}

		windowRef.current = viewerWindow;
		setViewerOpen(true);

		viewerWindow.document.write(buildViewerDocument());
		viewerWindow.document.close();

		const tick = () => {
			if (!windowRef.current || windowRef.current.closed) {
				stop();
				return;
			}
			refreshViewer(windowRef.current, window.dataLayer ?? []);
		};

		tick();
		intervalRef.current = setInterval(tick, REFRESH_INTERVAL);
	}, [stop]);

	const toggleViewer = useCallback(() => {
		if (windowRef.current && !windowRef.current.closed) {
			closeViewer();
			return;
		}
		openViewer();
	}, [closeViewer, openViewer]);

	useEffect(() => {
		return () => {
			if (windowRef.current && !windowRef.current.closed) {
				windowRef.current.close();
			}
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, []);

	return { toggleViewer, isViewerOpen };
};
